import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { isApiError } from '@/api/errors';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { platformApi } from '@/features/platform/api';
import { CheckCircleIcon, ExclamationTriangleIcon, EnvelopeIcon } from '@/icons';

const TOKEN_RE = /^nhi_[0-9a-f]{32}\.[A-Za-z0-9_-]{20,60}$/;

/**
 * Invitation acceptance. Reached from the one-time link an operator shares
 * after onboarding; RequireAuth sends signed-out visitors to /login and back.
 * The server only accepts when the signed-in account owns the invited email.
 */
export function AcceptInvitationPage() {
  const { token = '' } = useParams();
  const { user } = useAuth();
  const { switchTo } = useTenant();
  const qc = useQueryClient();
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const malformed = !TOKEN_RE.test(token);

  const accept = async () => {
    setState('busy'); setError(null);
    try {
      const res = await platformApi.acceptInvitation(token);
      await qc.invalidateQueries({ queryKey: ['auth', 'tenants'] });
      setTenantId(res.tenantId); setState('done');
    } catch (e) {
      setState('idle');
      if (isApiError(e) && e.code === 'INVITATION_EMAIL_MISMATCH') setError(`This invitation was issued to a different email address than ${user?.email ?? 'your account'}. Sign in with the invited account to accept it.`);
      else if (isApiError(e) && e.code === 'INVITATION_INVALID') setError('This invitation is invalid, has already been used, or has expired. Ask your Nifty Hosting contact for a new one.');
      else if (isApiError(e) && e.code === 'MEMBERSHIP_EXISTS') setError('You are already a member of this organization.');
      else setError(isApiError(e) ? e.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <div>
      <PageHeader title="Organization invitation" />
      <section className="max-w-xl rounded-xl border border-border bg-surface p-6">
        {malformed ? (
          <Notice tone="danger" title="This invitation link is not valid">Check that you copied the whole link, or ask for a new invitation.</Notice>
        ) : state === 'done' ? (
          <>
            <Notice tone="success" title="Invitation accepted">You now have administrator access to the organization.</Notice>
            <Button className="mt-5" color="primary" onPress={() => tenantId && void switchTo(tenantId)}>Open the organization</Button>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" />
              <div>
                <h2 className="text-base font-semibold text-fg">You have been invited to administer an organization</h2>
                <p className="mt-1 text-sm text-fg-muted">Signed in as <span className="font-medium text-fg">{user?.email}</span>. The invitation can only be accepted by the account it was sent to.</p>
              </div>
            </div>
            {error && <div role="alert" className="mt-4"><Notice tone="danger" title="Could not accept the invitation">{error}</Notice></div>}
            <div className="mt-6 flex gap-2">
              <Button color="primary" isLoading={state === 'busy'} onPress={() => void accept()}>Accept invitation</Button>
              <Link to="/" className="self-center px-3 text-sm text-fg-muted hover:text-fg">Not now</Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Notice({ tone, title, children }: { tone: 'success' | 'danger'; title: string; children: React.ReactNode }) {
  const Icon = tone === 'success' ? CheckCircleIcon : ExclamationTriangleIcon;
  return (
    <div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${tone === 'success' ? 'border-success/40 bg-success/5' : 'border-danger/40 bg-danger/5'}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone === 'success' ? 'text-success' : 'text-danger'}`} aria-hidden="true" />
      <div><p className="font-medium text-fg">{title}</p><p className="mt-0.5 text-fg-muted">{children}</p></div>
    </div>
  );
}
